import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import styles from './ForemanMessage.module.css';
import type { ChatMessage, ForemanParsedResponse } from '../../types/foreman';
import ConfidenceBadge from './ConfidenceBadge';
import SkillTag from './SkillTag';
import ChecklistItem from './ChecklistItem';
import PricingTable from './PricingTable';
import AlertCard from './AlertCard';
import ProgressSteps from './ProgressSteps';
import ChoiceCard from './ChoiceCard';
import ActionButtons from './ActionButtons';
import CorrectionCard from './CorrectionCard';
import PracticeCard from './PracticeCard';
import LearningReportCard from './LearningReportCard';
import { CorrectionButton } from '../training';
import type { CorrectionFormData } from '../training';
import { FeedbackButton } from '../FeedbackButton';

interface ForemanMessageProps {
  message: ChatMessage;
  onAction?: (action: string) => void;
  onChoiceSelect?: (choice: string) => void;
  onCorrect?: (messageId: string, data: CorrectionFormData) => void;
  /** Active thread session key — required for the per-message flag button.
   *  When null/undefined the flag button still renders but its submit will
   *  no-op with an error flash (a defensive guard — ChatArea only renders
   *  Foreman messages for an active session). */
  sessionKey?: string | null;
}

function formatTime(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const ForemanMessage: React.FC<ForemanMessageProps> = ({
  message,
  onAction,
  onChoiceSelect,
  onCorrect,
  sessionKey,
}) => {
  const [hovered, setHovered] = useState(false);
  const parsed: ForemanParsedResponse | undefined = message.parsed;

  const confidence = parsed?.meta?.confidence
    ? parseInt(parsed.meta.confidence, 10)
    : null;
  const skill = parsed?.meta?.skill ?? null;
  const source = parsed?.meta?.source ?? null;
  const text = parsed?.text ?? message.content;

  return (
    <div
      className={styles.wrapper}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={styles.avatar}>{'\u26CF'}</div>
      <div className={styles.bubble}>
        {/* Meta row: confidence badge, skill tag, source */}
        {(confidence !== null || skill) && (
          <div className={styles.topRow}>
            {confidence !== null && !isNaN(confidence) && (
              <ConfidenceBadge confidence={confidence} />
            )}
            {skill && <SkillTag skill={skill} />}
            {source && <span className={styles.source}>{source}</span>}
          </div>
        )}

        {/* Main text content */}
        {text && (
          <div className={styles.content}>
            <ReactMarkdown>{text}</ReactMarkdown>
          </div>
        )}

        {/* Structured blocks */}
        <div className={styles.blocks}>
          {/* Checklist items */}
          {parsed?.items && parsed.items.length > 0 && (
            <div>
              {parsed.items.map((item, idx) => (
                <ChecklistItem key={idx} item={item} />
              ))}
            </div>
          )}

          {/* Pricing table */}
          {parsed?.pricing && <PricingTable pricing={parsed.pricing} />}

          {/* Alert card */}
          {parsed?.alert && <AlertCard alert={parsed.alert} />}

          {/* Progress steps */}
          {parsed?.progress && parsed.progress.length > 0 && (
            <ProgressSteps steps={parsed.progress} />
          )}

          {/* Choice cards */}
          {parsed?.choices &&
            parsed.choices.length > 0 &&
            parsed.choices.map((choice, idx) => (
              <ChoiceCard
                key={idx}
                choice={choice}
                onSelect={onChoiceSelect}
              />
            ))}

          {/* Correction card */}
          {parsed?.correction && (
            <CorrectionCard correction={parsed.correction} />
          )}

          {/* Practice card */}
          {parsed?.practice && <PracticeCard practice={parsed.practice} />}

          {/* Learning report card */}
          {parsed?.learningReport && (
            <LearningReportCard report={parsed.learningReport} />
          )}

          {/* Action buttons */}
          {parsed?.actions && parsed.actions.length > 0 && (
            <ActionButtons actions={parsed.actions} onAction={onAction} />
          )}
        </div>

        {/* Footer with sender + timestamp */}
        <div className={styles.footer}>
          <span className={styles.senderName}>Foreman</span>
          {onCorrect && (
            <div className={styles.correctionBtn}>
              <CorrectionButton
                messageId={message.id}
                messageContent={text || message.content}
                skill={skill ?? undefined}
                visible={hovered}
                onSubmit={onCorrect}
              />
            </div>
          )}
          {/* Inline flag button — see FeedbackButton + POST /api/auth/feedback/report.
              Hover-revealed alongside "Correct this" so designers don't have
              to leave the chat to report a bad answer. */}
          <div className={styles.correctionBtn}>
            <FeedbackButton
              messageId={message.id}
              sessionKey={sessionKey ?? null}
              visible={hovered}
            />
          </div>
          <span className={styles.timestamp}>
            {formatTime(message.timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ForemanMessage;
